import { ChangeEvent, Fragment, KeyboardEvent, UIEvent, useState, useRef, useEffect, useMemo } from "react"
import "./styles.less"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import {
    colorAttentionBlue50,
    colorFunctionalGray50,
    colorFunctionalGray10,
    colorFunctionalGray70,
} from "@rhumbix/rmbx_design_system_web"
import styled from "styled-components"

type AutocompleteSuggestion = {
    token: string
    expression: {
        description: string
    }
}

// Note: Could be made more generic by adding a getDescription() prop.
type AutocompleteInputProps<T extends AutocompleteSuggestion> = {
    id?: string
    allOptions: T[]
    value: string
    onValueChange?: (value: string) => void
    optionCallback?: (value: string) => T[]
    onDetailsRequested?: (option: T) => void
    placeholder?: string
    readOnly?: boolean // Hides the input
    disabled?: boolean // Disables the input
    focusOnMount?: boolean
}

export const tokenizeBraces = (input: string): string[] => {
    const tokens = []
    let currentToken = ""
    let numOpenBraces = 0
    let numCloseBraces = 0

    for (let i = 0; i < input.length; i++) {
        const char = input[i]
        if (char === "{") {
            if (!numOpenBraces) {
                if (currentToken) tokens.push(currentToken)
                currentToken = ""
                numCloseBraces = 0
            }
            numOpenBraces++
            currentToken += char
        } else if (char === "}") {
            numCloseBraces++
            currentToken += char
            // We're at the end of something
            if ((i < input.length - 1 && input[i + 1] !== "}") || i == input.length - 1) {
                if (numOpenBraces === numCloseBraces) {
                    tokens.push(currentToken)
                    currentToken = ""
                    numOpenBraces = 0
                    numCloseBraces = 0
                }
            }
        } else {
            currentToken += char
        }
    }
    if (currentToken) tokens.push(currentToken)
    return tokens
}

const AutocompleteInput = <T extends AutocompleteSuggestion>(props: AutocompleteInputProps<T>): JSX.Element => {
    const { value } = props
    const ref = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const selectedOptionRef = useRef<HTMLDivElement | null>(null)
    const [showDropdown, setShowDropdown] = useState(false)
    const [filteredOptions, setFilteredOptions] = useState<T[]>([])
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [newCursorPosition, setNewCursorPosition] = useState(value?.length || 0)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current!.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current!.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (props.focusOnMount) {
            inputRef.current?.focus()
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    /**
     * Set the cursor position after an autocomplete was chosen so that it doesn't jump to the end
     */
    useEffect(() => {
        if (newCursorPosition) {
            inputRef.current?.setSelectionRange(newCursorPosition, newCursorPosition)
            setNewCursorPosition(0)
        }
    }, [newCursorPosition])

    useEffect(() => {
        // Scroll the selected option into view
        if (selectedOptionRef.current) {
            selectedOptionRef.current!.scrollIntoView({ block: "nearest", behavior: "smooth" })
        }
    }, [selectedIndex])

    const syncScroll = (e: UIEvent<HTMLInputElement>) => {
        if (ref.current) {
            ref.current!.scrollTop = (e.target as HTMLInputElement).scrollTop
            ref.current!.scrollLeft = (e.target as HTMLInputElement).scrollLeft
        }
    }

    /**
     * A user has selected an autocomplete option from the dropdown.
     * @param option The option selected
     */
    const handleOptionSelect = (option: T) => {
        const currentPosition = inputRef.current?.selectionStart || 0
        const strBeforeCursor = value.substring(0, currentPosition)
        // Find the position of the last '{'
        const lastBraceIndex = strBeforeCursor.lastIndexOf("{")
        if (lastBraceIndex !== -1) {
            // Replace everything after '{' up to the current cursor position with the selected option and add '}'
            const restOfStringStart = value.charAt(currentPosition) === "}" ? currentPosition + 1 : currentPosition
            const newValue =
                value.substring(0, lastBraceIndex + 1) + option.token + "}" + value.substring(restOfStringStart)
            // After the new value is set, let's move the cursor to the end of the newly inserted field
            const cursorPosition = lastBraceIndex + option.token.length + 2
            setNewCursorPosition(cursorPosition)
            props.onValueChange?.(newValue)
        }
        setShowDropdown(false)
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        props.onValueChange?.(value)

        const currentPosition = e.target.selectionStart
        const strBeforeCursor = value.substring(0, currentPosition || 0)
        const tokens = tokenizeBraces(strBeforeCursor.replace(/{{/g, "").replace(/}}/g, ""))

        // Check if we're on a new token
        if (tokens.length && tokens[tokens.length - 1].startsWith("{")) {
            const lastCurlyIndex = strBeforeCursor.lastIndexOf("{")
            const matchStr = strBeforeCursor.substring(lastCurlyIndex + 1)
            setShowDropdown(true)
            setFilteredOptions(props.optionCallback!(matchStr))
            setSelectedIndex(0)
        } else {
            setShowDropdown(false)
        }
    }

    /**
     * Do some key handling to allow for navigating the autocomplete dropdown.
     * @param e
     */
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) return

        switch (e.key) {
            case "ArrowDown":
                // Prevents moving the cursor to the end of the field
                e.preventDefault()
                setSelectedIndex(prev => (prev + 1) % filteredOptions.length)
                break
            case "ArrowUp":
                // Prevents moving the cursor to the beginning of the field
                e.preventDefault()
                setSelectedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length)
                break
            case "Enter":
                if (filteredOptions[selectedIndex]) {
                    handleOptionSelect(filteredOptions[selectedIndex])
                } else {
                    props.onValueChange?.(value)
                }
                break
            case "Escape":
                setShowDropdown(false)
                break
            default:
        }
    }
    const tooltipSpot = document.getElementById("master-container")
    const tokens = tokenizeBraces(value)
    const tokenSet = useMemo(() => new Set(props.allOptions.map(t => t.token)), [props.allOptions])

    return (
        <div
            className={`autocomplete-input-container ${props.readOnly ? "read-only" : ""}`}
            style={{ backgroundColor: props.disabled ? colorFunctionalGray10 : undefined }}
        >
            {!props.readOnly && (
                <input
                    className={"autocomplete-input-editor"}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onScroll={syncScroll}
                    placeholder={props.placeholder}
                    ref={inputRef}
                    id={props.id}
                    spellCheck={false}
                    disabled={props.disabled}
                    value={value}
                />
            )}
            <div
                ref={ref}
                className={`autocomplete-input-renderer ${props.readOnly ? "read-only" : ""}`}
                style={{ color: props.disabled ? colorFunctionalGray70 : undefined }}
            >
                {tokens.map((word, i) => {
                    const cleanToken = word.replace(/{{/g, "").replace(/}}/g, "")
                    const numOpenCurlies = (word.replace(/{{/g, "").match(/[^{]?\{/g) || []).length
                    const numCloseCurlies = (word.replace(/}}/g, "").match(/[^}]?\}/g) || []).length
                    if (!(numOpenCurlies % 2) && (!(numCloseCurlies % 2) || !numOpenCurlies)) {
                        return <span key={i}>{word}</span>
                    }
                    const maybeToken = cleanToken.slice(1, -1).split(":")[0]
                    return (
                        <span key={i} className={tokenSet.has(maybeToken) ? "completed-template" : "bad-template"}>
                            {word}
                        </span>
                    )
                })}
            </div>
            {showDropdown && (
                <div ref={dropdownRef} role="listbox" className="autocomplete-dropdown">
                    {filteredOptions.map((option, index) => (
                        <Fragment key={option.token}>
                            <div
                                ref={index === selectedIndex ? selectedOptionRef : null}
                                className={`dropdown-option ${index === selectedIndex ? "selected" : ""}`}
                                onClick={() => handleOptionSelect(option)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        handleOptionSelect(option)
                                    }
                                }}
                                role="option"
                                aria-selected={index === selectedIndex}
                                tabIndex={0}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        paddingRight: 0,
                                        position: "relative",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "100%",
                                            flexGrow: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <TokenName>{option.token}</TokenName>
                                        <TokenDescription data-tooltip-id={`token-${option.token}`}>
                                            {option.expression.description}
                                        </TokenDescription>
                                    </div>
                                    {props.onDetailsRequested ? (
                                        <DocumentationContainer>
                                            <DocumentationLink
                                                type="button"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    props.onDetailsRequested?.(option)
                                                }}
                                            >
                                                Details
                                            </DocumentationLink>
                                        </DocumentationContainer>
                                    ) : null}
                                </div>
                            </div>
                            <Portal container={tooltipSpot}>
                                <ReactTooltip
                                    id={`token-${option.token}`}
                                    style={{ zIndex: 100000, maxWidth: "500px" }}
                                    float={true}
                                    place="top"
                                    variant="dark"
                                    content={option.expression.description}
                                />
                            </Portal>
                        </Fragment>
                    ))}
                </div>
            )}
        </div>
    )
}

const TokenName = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TokenDescription = styled.span`
    color: ${colorFunctionalGray50};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const DocumentationContainer = styled.div`
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: fit-content;
`

const DocumentationLink = styled.button`
    color: ${colorAttentionBlue50};
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    padding: 0;
    margin: 0;
    width: 48px;
    min-width: 48px;

    &:hover {
        text-decoration: underline;
    }
`

export default AutocompleteInput
